import { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'

interface SavedArticle {
  _id: string
  userId: string
  articleId: string
  savedAt: string
}

export function useSavedArticles() {
  const [savedArticles, setSavedArticles] = useState<SavedArticle[]>([])
  const [loading, setLoading] = useState(false)

  const fetchSaved = async () => {
    if (!lumi.auth.isAuthenticated) {
      setSavedArticles([])
      return
    }

    try {
      setLoading(true)
      const result = await lumi.entities.saved_articles.list({
        sort: { savedAt: -1 },
        limit: 1000
      })
      setSavedArticles(result?.list || [])
    } catch (error) {
      console.error('Failed to fetch saved articles:', error)
      setSavedArticles([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSaved()
  }, [])

  const isSaved = (articleId: string): boolean => {
    return savedArticles.some(saved => saved.articleId === articleId)
  }

  const toggleSave = async (articleId: string) => {
    if (!lumi.auth.isAuthenticated) {
      lumi.auth.signInWithOAuth({
        provider: 'google',
        onSuccess: () => {
          console.log('Login successful')
        }
      })
      return
    }

    const saved = savedArticles.find(s => s.articleId === articleId)
    
    try {
      if (saved) {
        await lumi.entities.saved_articles.delete(saved._id)
        setSavedArticles(prev => prev.filter(s => s._id !== saved._id))
      } else {
        const now = new Date().toISOString()
        const newSaved = await lumi.entities.saved_articles.create({
          userId: lumi.auth.user?.userId || '',
          articleId,
          savedAt: now,
          creator: lumi.auth.user?.userId || '',
          createdAt: now,
          updatedAt: now
        })
        setSavedArticles(prev => [newSaved, ...prev])
      }
    } catch (error) {
      console.error('Failed to toggle save:', error)
    }
  }

  return {
    savedArticles,
    loading,
    isSaved,
    toggleSave,
    refetch: fetchSaved
  }
}
